import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { Suspense, useEffect } from "react";
import { useFragment, useQueryLoader } from "react-relay";

import { VStack } from "@whoosh/design-system/src/components/primitive";

import { currentBookingAtom } from "../../../../atoms/stores";
import { EditSectionSkeletonLoader } from "../EditSectionSkeletonLoader/EditSectionSkeletonLoader";
import {
  BookingDurationCourtInner,
  BookingDurationCourtInnerQueryString,
} from "./BookingDurationCourtInner";
import { BookingDurationCourtFragment$key } from "./__generated__/BookingDurationCourtFragment.graphql";
import { BookingDurationCourtInnerQuery } from "./__generated__/BookingDurationCourtInnerQuery.graphql";

type BookingDurationCourtProps = {
  slotRef: BookingDurationCourtFragment$key | null;
  durationMins: number;
  isLessonBooking: boolean;
  currentSessionUserClientId: string;
};

export const BookingDurationCourt = ({
  slotRef,
  durationMins,
  isLessonBooking,
  currentSessionUserClientId,
}: BookingDurationCourtProps) => {
  const [queryRef, loadQuery] = useQueryLoader<BookingDurationCourtInnerQuery>(
    BookingDurationCourtInnerQueryString
  );

  const slot = useFragment(BookingDurationCourtFragmentString, slotRef);
  const currentBooking = useAtomValue(currentBookingAtom);
  const bookingId = currentBooking?.id;

  const canSelectCourt =
    slot?.agenda?.facility?.publicBookingPermissionSet?.customerCanSelectCourt;

  // check if the current court is in a course bucket
  const isInCourseBucket = !!slot?.course?.courseBucket?.id;
  const willShowCourseSelection = canSelectCourt && isInCourseBucket;

  useEffect(() => {
    if (slot?.course?.id && slot?.time && slot?.agenda?.id && bookingId) {
      loadQuery(
        {
          courseId: slot?.course?.id ?? "",
          startTime: slot?.time ?? "",
          agendaId: slot?.agenda?.id ?? "",
          slotId: slot?.id ?? "",
          bookingType: isLessonBooking ? "LESSON" : "STANDARD",
          memberId: currentSessionUserClientId,
          bookingId: bookingId,
        },
        {
          fetchPolicy: "store-and-network",
        }
      );
    }
  }, [slot, loadQuery, isLessonBooking, currentSessionUserClientId, bookingId]);

  return (
    <Suspense
      fallback={
        willShowCourseSelection ? (
          <VStack>
            <EditSectionSkeletonLoader /> <EditSectionSkeletonLoader />
          </VStack>
        ) : (
          <EditSectionSkeletonLoader />
        )
      }
    >
      {queryRef && slot ? (
        <BookingDurationCourtInner
          queryRef={queryRef}
          slotRef={slot}
          durationMins={durationMins}
          isLessonBooking={isLessonBooking}
        />
      ) : null}
    </Suspense>
  );
};
const BookingDurationCourtFragmentString = graphql`
  fragment BookingDurationCourtFragment on Slot {
    id
    ...BookingDurationCourtInnerFragment
    ... on TimeSlot {
      course {
        id
        courseBucket {
          id
        }
      }
      time
    }
    agenda {
      id
      facility {
        publicBookingPermissionSet {
          customerCanSelectCourt
        }
      }
    }
  }
`;
